/**
 * The login **"what's new"** modal (Issue #185): an announcement card the captain can
 * post without a client code change. It appears **once per login**, right after the
 * player enters the bank - regardless of whether that exact announcement was shown
 * before (ADR-0113 dropped the earlier "don't nag until a newer `id`" gate, so every
 * login is a fresh chance to see the current card; the player dismisses it each time).
 *
 * The manifest is a **list of pages**, newest first: the modal opens on the most
 * recent patch note and the player pages back through older ones with the Next / Prev
 * controls (a single-page manifest simply hides the nav). Each page is one
 * {@link Announcement}.
 *
 * Like the rest of the client chrome (chat, the leaderboard window) this module owns
 * only DOM wiring - the markup + styling live in `index.html`, so it is headless-
 * testable under jsdom and reads no Pixi and no socket. The announcement content is
 * fetched by `main.ts` from the server's `GET /whatsnew.json` route (mirroring
 * `fetchLeaderboard`) and handed here at the Welcome moment.
 *
 * Two disciplines matter here:
 * - **Safe rendering.** The body is captain-authored but served over the network, so
 *   it is treated as untrusted: it is rendered as plain text (paragraphs split on
 *   blank lines, single newlines become `<br>`) built with `createTextNode`/
 *   `createElement`, never `innerHTML`. A body like `<img onerror=…>` shows verbatim
 *   as text and is never parsed as markup (the same XSS discipline the chat journal
 *   follows). Rich markdown is a deliberate future extension (ADR-0099).
 * - **Show once per page load.** The card shows for any present, well-formed
 *   announcement, but the {@link WhatsNewHandle.maybeShow} once-per-load guard keeps a
 *   reconnect's repeat Welcome from re-opening it within the same session. Dismissal
 *   (button, backdrop click, or Escape) just closes it for this session; nothing is
 *   persisted, so the next login shows the current card again.
 */

/** One published announcement: an id that drives dismissal, plus the shown content. */
export interface Announcement {
  /**
   * A monotonic/unique string (date-stamped by convention, e.g. `2026-06-launch`)
   * that identifies the page. Historically drove dismissal (ADR-0113 dropped that
   * gate); today it is just a stable key.
   */
  id: string;
  /** The modal heading. */
  title: string;
  /** A short display date shown under the title (e.g. `7/2/26`). Optional. */
  date?: string;
  /** The body copy (plain text; blank lines split paragraphs). Optional. */
  body?: string;
}

/** Options for {@link initWhatsNew}; the document is injected so tests can pass jsdom's. */
export interface WhatsNewOptions {
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts` keeps to (maybe) show the modal once the player has entered. */
export interface WhatsNewHandle {
  /**
   * Show the modal for `announcements` - the newest-first list of patch-note pages -
   * iff it holds at least one well-formed page, and only once per page load. Opens on
   * the newest page (index 0). Returns whether the modal was actually shown (handy for
   * tests). A `null`/empty/all-malformed list (a failed or empty fetch) shows nothing,
   * so an outage never blocks entry. A single {@link Announcement} is also accepted for
   * convenience and treated as a one-page list.
   */
  maybeShow(announcements: Announcement[] | Announcement | null | undefined): boolean;
  /** Whether the modal is currently open. */
  isOpen(): boolean;
  /** Dismiss the modal (closes it for this session), as the button does. */
  close(): void;
}

/** A well-formed announcement carries a non-empty `id` and `title`. */
function isValid(a: Announcement | null | undefined): a is Announcement {
  return (
    a !== null &&
    a !== undefined &&
    typeof a.id === 'string' &&
    a.id.length > 0 &&
    typeof a.title === 'string' &&
    a.title.length > 0
  );
}

/**
 * Wire the `#whatsnew` overlay (declared hidden in `index.html`) into the paged
 * announcement modal. The core parts (`#whatsnew-title`, `#whatsnew-body`,
 * `#whatsnew-dismiss`) must exist; if any is missing the returned handle is a harmless
 * no-op so a missing-markup client never throws (mirrors {@link initLeaderboardWindow}).
 * The paging parts (`#whatsnew-date`, `#whatsnew-nav`, `#whatsnew-prev`,
 * `#whatsnew-next`, `#whatsnew-counter`) are optional: absent, the modal still shows a
 * single page.
 */
export function initWhatsNew(options: WhatsNewOptions = {}): WhatsNewHandle {
  const doc = options.doc ?? document;
  const overlay = doc.getElementById('whatsnew');
  const titleEl = doc.getElementById('whatsnew-title');
  const bodyEl = doc.getElementById('whatsnew-body');
  const dismissBtn = doc.getElementById('whatsnew-dismiss');
  if (!overlay || !titleEl || !bodyEl || !dismissBtn) {
    return { maybeShow: () => false, isOpen: () => false, close: () => {} };
  }
  // Paging chrome is optional - a client without it still shows the newest page.
  const dateEl = doc.getElementById('whatsnew-date');
  const contentEl = overlay.querySelector<HTMLElement>('.whatsnew-content');
  const navEl = doc.getElementById('whatsnew-nav');
  const prevBtn = doc.getElementById('whatsnew-prev') as HTMLButtonElement | null;
  const nextBtn = doc.getElementById('whatsnew-next') as HTMLButtonElement | null;
  const counterEl = doc.getElementById('whatsnew-counter');

  // At-most-once-per-page-load: a reconnect that re-fires `onWelcome` must not re-open
  // the modal within the same session.
  let shownThisLoad = false;
  // The newest-first pages currently loaded and which one is on screen (0 = newest).
  let pages: Announcement[] = [];
  let pageIndex = 0;

  /** Render safe plain text into paragraphs and simple dash-prefixed update lists. */
  const renderBody = (body: string | undefined): void => {
    bodyEl.textContent = '';
    if (!body) return;

    const lines = body.split(/\r?\n/);
    let list: HTMLUListElement | null = null;
    const finishList = (): void => {
      list = null;
    };

    lines.forEach((rawLine, index) => {
      const line = rawLine.trim();
      if (!line) {
        finishList();
        return;
      }

      if (line.startsWith('- ')) {
        if (!list) {
          list = doc.createElement('ul');
          list.className = 'whatsnew-list';
          bodyEl.appendChild(list);
        }
        const item = doc.createElement('li');
        item.textContent = line.slice(2);
        list.appendChild(item);
        return;
      }

      finishList();
      const p = doc.createElement('p');
      p.textContent = line;
      if (index === 0) p.className = 'whatsnew-lede';
      if (index === lines.length - 1 && /[!.]$/.test(line)) p.className = 'whatsnew-signoff';
      bodyEl.appendChild(p);
    });
  };

  /** Paint the current page (title/date/body) and refresh the nav row. */
  const renderPage = (): void => {
    const page = pages[pageIndex];
    if (!page) return;
    // titleEl/dateEl are textContent (never innerHTML), so content can't inject markup.
    titleEl.textContent = page.title;
    if (dateEl) dateEl.textContent = page.date ?? '';
    renderBody(page.body);

    // The nav row only earns its keep with more than one page; otherwise it stays out
    // of the way entirely. Newest page (index 0) has nothing newer; oldest has nothing
    // older, so the reach-the-end button disables rather than wrapping.
    if (navEl) navEl.hidden = pages.length <= 1;
    if (prevBtn) prevBtn.disabled = pageIndex <= 0;
    if (nextBtn) nextBtn.disabled = pageIndex >= pages.length - 1;
    if (counterEl) counterEl.textContent = `${pageIndex + 1} / ${pages.length}`;
    // The article is the scrolling region. Reset it on every page so an older note
    // never opens at the previous note's reading position.
    (contentEl ?? bodyEl).scrollTop = 0;
  };

  /** Step by `delta` pages (Next = +1 toward older), clamped to the list. */
  const goTo = (index: number): void => {
    const clamped = Math.max(0, Math.min(pages.length - 1, index));
    if (clamped === pageIndex) return;
    pageIndex = clamped;
    renderPage();
  };

  const close = (): void => {
    overlay.hidden = true;
  };

  const maybeShow = (
    announcements: Announcement[] | Announcement | null | undefined,
  ): boolean => {
    if (shownThisLoad) return false;
    const list = Array.isArray(announcements)
      ? announcements
      : announcements
        ? [announcements]
        : [];
    const valid = list.filter(isValid);
    if (valid.length === 0) return false;

    pages = valid;
    pageIndex = 0;
    renderPage();
    shownThisLoad = true;
    overlay.hidden = false;
    // Move focus onto the dismiss button so Enter/Escape work and the modal is
    // keyboard/screen-reader reachable the instant it opens.
    try {
      (dismissBtn as HTMLButtonElement).focus();
    } catch {
      /* focus can throw in some headless envs; the modal is still usable. */
    }
    return true;
  };

  // Three dismiss gestures, all of which close the modal for this session: the "Got it"
  // button, a click on the backdrop (the overlay itself, outside the card), and Escape.
  dismissBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    // Only a click on the backdrop closes; clicks that land on the card bubble up to
    // the overlay but must not dismiss (target is the card or its children then).
    if (e.target === overlay) close();
  });
  // Next pages back to older notes, Prev returns toward the newest.
  prevBtn?.addEventListener('click', () => goTo(pageIndex - 1));
  nextBtn?.addEventListener('click', () => goTo(pageIndex + 1));
  doc.addEventListener('keydown', (e) => {
    if (overlay.hidden) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(pageIndex + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(pageIndex - 1);
    }
  });

  return { maybeShow, isOpen: () => !overlay.hidden, close };
}
